import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from './Header';
import Pagination from './Pagination';
import { 
  Home, Building2, Eye, Search, Filter, Download, 
  TrendingUp, TrendingDown, Landmark, Package,
  CheckCircle, XCircle, AlertCircle, RefreshCw,
  BarChart3, PieChart, Table, Grid3x3, Map,
  DollarSign, Maximize2, ChevronRight, ChevronDown
} from 'lucide-react';

const ViewSoldAndUnsoldPlots = () => {
    const server_app_api_php = window.SERVER_APP_API_PHP;
    
    const [activeTab, setActiveTab] = useState('ridgehill-unsold');
    const [plotsRidgehill, setPlotsRidgehill] = useState([]);
    const [plotsRidgehillSold, setPlotsRidgehillSold] = useState([]);
    const [plotsHardstone, setPlotsHardstone] = useState([]);
    const [plotsHardstoneSold, setPlotsHardstoneSold] = useState([]);
    
    // Pagination and filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('table');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [filteredData, setFilteredData] = useState([]);
    
    const [loading, setLoading] = useState({
        ridgehill: false,
        ridgehillSold: false,
        hardstone: false,
        hardstoneSold: false
    });
    
    const [stats, setStats] = useState({
        ridgehillTotal: 0,
        ridgehillSold: 0,
        ridgehillUnsold: 0,
        hardstoneTotal: 0,
        hardstoneSold: 0,
        hardstoneUnsold: 0
    });

    useEffect(() => {
        fetchAllData();
    }, []);

    useEffect(() => {
        calculateStats();
    }, [plotsRidgehill, plotsRidgehillSold, plotsHardstone, plotsHardstoneSold]);

    useEffect(() => {
        // Filter data whenever search query or active tab changes
        filterData();
        setCurrentPage(1); // Reset to first page when search changes
    }, [searchQuery, activeTab, plotsRidgehill, plotsRidgehillSold, plotsHardstone, plotsHardstoneSold]);

    const filterData = () => {
        const searchLower = searchQuery.toLowerCase();
        let dataToFilter = [];
        
        switch(activeTab) {
            case 'ridgehill-unsold':
                dataToFilter = plotsRidgehill;
                break;
            case 'ridgehill-sold':
                dataToFilter = plotsRidgehillSold;
                break;
            case 'hardstone-unsold':
                dataToFilter = plotsHardstone;
                break;
            case 'hardstone-sold':
                dataToFilter = plotsHardstoneSold;
                break;
            default:
                dataToFilter = [];
        }

        if (searchLower.trim() === '') {
            setFilteredData(dataToFilter);
        } else {
            const filtered = dataToFilter.filter(plot => 
                plot.plotNumber?.toString().toLowerCase().includes(searchLower) ||
                plot.plotNumberNew?.toString().toLowerCase().includes(searchLower) ||
                plot.plotArea?.toString().toLowerCase().includes(searchLower) ||
                plot.plotAreasNew?.toString().toLowerCase().includes(searchLower) ||
                plot.plotPrice?.toString().toLowerCase().includes(searchLower) ||
                plot.plotPricesNew?.toString().toLowerCase().includes(searchLower)
            );
            setFilteredData(filtered);
        }
    };

    const fetchAllData = async () => {
        await Promise.all([
            fetchDataRidgehill(),
            fetchDataRidgehillSoldPlots(),
            fetchDataHardstone(),
            fetchDataHardstoneSoldPlots()
        ]);
    };

    const fetchDataRidgehill = async () => {
        setLoading(prev => ({...prev, ridgehill: true}));
        const formData = new FormData();
        formData.append('apiType', 'fetchUnsoldRidgehillPlots');
        try {
            const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
            setPlotsRidgehill(response.data);
        } catch (error) {
            console.error('Error fetching Ridgehill plots:', error);
        } finally {
            setLoading(prev => ({...prev, ridgehill: false}));
        }
    };

    const fetchDataRidgehillSoldPlots = async () => {
        setLoading(prev => ({...prev, ridgehillSold: true}));
        const formData = new FormData();
        formData.append('apiType', 'fetchSoldRidgehillPlots');
        try {
            const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
            setPlotsRidgehillSold(response.data);
        } catch (error) {
            console.error('Error fetching Ridgehill sold plots:', error);
        } finally {
            setLoading(prev => ({...prev, ridgehillSold: false}));
        }
    };

    const fetchDataHardstone = async () => {
        setLoading(prev => ({...prev, hardstone: true}));
        const formData = new FormData();
        formData.append('apiType', 'fetchUnsoldHardstonePlots');
        try {
            const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
            setPlotsHardstone(response.data);
        } catch (error) {
            console.error('Error fetching Hardstone plots:', error);
        } finally {
            setLoading(prev => ({...prev, hardstone: false}));
        }
    };

    const fetchDataHardstoneSoldPlots = async () => {
        setLoading(prev => ({...prev, hardstoneSold: true}));
        const formData = new FormData();
        formData.append('apiType', 'fetchSoldHardstonePlots');
        try {
            const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
            setPlotsHardstoneSold(response.data);
        } catch (error) {
            console.error('Error fetching Hardstone sold plots:', error);
        } finally {
            setLoading(prev => ({...prev, hardstoneSold: false}));
        }
    };

    const calculateStats = () => {
        setStats({
            ridgehillTotal: plotsRidgehill.length + plotsRidgehillSold.length,
            ridgehillSold: plotsRidgehillSold.length,
            ridgehillUnsold: plotsRidgehill.length,
            hardstoneTotal: plotsHardstone.length + plotsHardstoneSold.length,
            hardstoneSold: plotsHardstoneSold.length,
            hardstoneUnsold: plotsHardstone.length
        });
    };

    const formatCurrency = (amount) => {
        if (!amount) return 'ZMW 0';
        return `ZMW ${parseFloat(amount).toLocaleString('en-ZM')}`;
    };

    // Calculate pagination
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1); // Reset to first page when rows per page changes
    };

    const getCurrentLoading = () => {
        switch(activeTab) {
            case 'ridgehill-unsold': return loading.ridgehill;
            case 'ridgehill-sold': return loading.ridgehillSold;
            case 'hardstone-unsold': return loading.hardstone;
            case 'hardstone-sold': return loading.hardstoneSold;
            default: return false;
        }
    };

    const renderStatsCard = () => {
        const currentTab = activeTab;
        const isRidgehill = currentTab.includes('ridgehill');
        const isSold = currentTab.includes('sold');
        
        if (isRidgehill) {
            return isSold ? stats.ridgehillSold : stats.ridgehillUnsold;
        } else {
            return isSold ? stats.hardstoneSold : stats.hardstoneUnsold;
        }
    };

    const renderTable = () => {
        const isLoading = getCurrentLoading();

        if (isLoading) {
            return (
                <div className="flex justify-center items-center py-20">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading plots data...</p>
                    </div>
                </div>
            );
        }

        if (filteredData.length === 0) {
            return (
                <div className="text-center py-12">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No plots found</h3>
                    <p className="text-gray-500">Try adjusting your search criteria</p>
                </div>
            );
        }

        return (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                SN
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Plot Details
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Size
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Price
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {currentRows.map((plot, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {indexOfFirstRow + index + 1}
                                </td>
                                <td className="px-6 py-4">
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">
                                            New: {plot.plotNumberNew || 'N/A'}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Old: {plot.plotNumber || 'N/A'}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div className="text-sm text-gray-900">
                                            New: {plot.plotAreasNew || 'N/A'} sq.m
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Old: {plot.plotArea || 'N/A'} sq.m
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div className="text-sm font-medium text-green-600">
                                            New: {formatCurrency(plot.plotPricesNew)}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Old: {formatCurrency(plot.plotPrice)}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                        activeTab.includes('sold') 
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {activeTab.includes('sold') ? (
                                            <>
                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                Sold
                                            </>
                                        ) : (
                                            <>
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                Available
                                            </>
                                        )}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Pagination Component */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    totalItems={filteredData.length}
                />
            </div>
        );
    };

    const renderGridView = () => {
        const isLoading = getCurrentLoading();

        if (isLoading) {
            return (
                <div className="flex justify-center items-center py-20">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading plots data...</p>
                    </div>
                </div>
            );
        }

        if (filteredData.length === 0) {
            return (
                <div className="text-center py-12">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No plots found</h3>
                    <p className="text-gray-500">Try adjusting your search criteria</p>
                </div>
            );
        }

        return (
            <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentRows.map((plot, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <div className="flex items-center">
                                            <Landmark className="w-5 h-5 text-blue-500 mr-2" />
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                Plot {plot.plotNumberNew || plot.plotNumber}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {plot.plotNumber && plot.plotNumber !== plot.plotNumberNew && 
                                                `Old: ${plot.plotNumber}`}
                                        </p>
                                    </div>
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                        activeTab.includes('sold') 
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {activeTab.includes('sold') ? 'Sold' : 'Available'}
                                    </span>
                                </div>
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Size</label>
                                        <div className="grid grid-cols-2 gap-2 mt-1">
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <p className="text-sm text-gray-500">New</p>
                                                <p className="font-medium">{plot.plotAreasNew || 'N/A'} sq.m</p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <p className="text-sm text-gray-500">Old</p>
                                                <p className="font-medium">{plot.plotArea || 'N/A'} sq.m</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase">Price</label>
                                        <div className="grid grid-cols-2 gap-2 mt-1">
                                            <div className="bg-green-50 p-3 rounded-lg">
                                                <p className="text-sm text-green-600">New Price</p>
                                                <p className="font-medium text-green-700">
                                                    {formatCurrency(plot.plotPricesNew)}
                                                </p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <p className="text-sm text-gray-500">Old Price</p>
                                                <p className="font-medium">
                                                    {formatCurrency(plot.plotPrice)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pagination Component for Grid View */}
                <div className="mt-6">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        totalItems={filteredData.length}
                    />
                </div>
            </>
        );
    };

    const tabs = [
        { id: 'ridgehill-unsold', label: 'Ridgehill Unsold', estate: 'Ridgehill', status: 'Available' },
        { id: 'ridgehill-sold', label: 'Ridgehill Sold', estate: 'Ridgehill', status: 'Sold' },
        { id: 'hardstone-unsold', label: 'Hardstone Unsold', estate: 'Hardstone', status: 'Available' },
        { id: 'hardstone-sold', label: 'Hardstone Sold', estate: 'Hardstone', status: 'Sold' }
    ];

    const getCurrentData = () => {
        switch(activeTab) {
            case 'ridgehill-unsold': return plotsRidgehill;
            case 'ridgehill-sold': return plotsRidgehillSold;
            case 'hardstone-unsold': return plotsHardstone;
            case 'hardstone-sold': return plotsHardstoneSold;
            default: return [];
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            
            {/* Main Content */}
            <main className="lg:ml-64 pt-16">
                <div className="p-6">
                    {/* Page Header */}
                    <div className="mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Plot Inventory</h1>
                                <p className="text-gray-600">View sold and unsold plots across estates</p>
                            </div>
                            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                                <button
                                    onClick={fetchAllData}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh
                                </button>
                                <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                                    <Download className="w-4 h-4 mr-2" />
                                    Export
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Stats Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Ridgehill Plots</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.ridgehillTotal}</p>
                                </div>
                                <div className="p-3 bg-blue-100 rounded-lg">
                                    <Building2 className="w-6 h-6 text-blue-600" />
                                </div>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                                <span className="text-sm text-green-600 flex items-center">
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    {stats.ridgehillSold} sold
                                </span>
                                <span className="text-sm text-blue-600 flex items-center">
                                    <AlertCircle className="w-4 h-4 mr-1" />
                                    {stats.ridgehillUnsold} available
                                </span>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Hardstone Plots</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.hardstoneTotal}</p>
                                </div>
                                <div className="p-3 bg-purple-100 rounded-lg">
                                    <Landmark className="w-6 h-6 text-purple-600" />
                                </div>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                                <span className="text-sm text-green-600 flex items-center">
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    {stats.hardstoneSold} sold
                                </span>
                                <span className="text-sm text-blue-600 flex items-center">
                                    <AlertCircle className="w-4 h-4 mr-1" />
                                    {stats.hardstoneUnsold} available
                                </span>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Overall Sold Rate</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {stats.ridgehillTotal + stats.hardstoneTotal > 0 
                                            ? Math.round(((stats.ridgehillSold + stats.hardstoneSold) / 
                                                (stats.ridgehillTotal + stats.hardstoneTotal)) * 100) 
                                            : 0}%
                                    </p>
                                </div>
                                <div className="p-3 bg-green-100 rounded-lg">
                                    <TrendingUp className="w-6 h-6 text-green-600" />
                                </div>
                            </div>
                            <div className="mt-4">
                                <div className="flex items-center text-sm text-gray-500">
                                    <span>Total plots across all estates</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Current View</p>
                                    <p className="text-2xl font-bold text-gray-900">{renderStatsCard()}</p>
                                    <p className="text-sm text-gray-500 capitalize">
                                        {tabs.find(t => t.id === activeTab)?.status} plots
                                    </p>
                                </div>
                                <div className="p-3 bg-orange-100 rounded-lg">
                                    <Eye className="w-6 h-6 text-orange-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        {/* Header with Tabs */}
                        <div className="border-b border-gray-200">
                            <div className="px-6 py-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                                    <div className="flex space-x-4 mb-4 sm:mb-0">
                                        {tabs.map(tab => (
                                            <button
                                                key={tab.id}
                                                onClick={() => {
                                                    setActiveTab(tab.id);
                                                    setCurrentPage(1); // Reset to first page when tab changes
                                                }}
                                                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                                                    activeTab === tab.id
                                                        ? 'bg-blue-600 text-white'
                                                        : 'text-gray-600 hover:bg-gray-100'
                                                }`}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    <div className="flex items-center space-x-4">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                placeholder="Search plots..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                        
                                        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                                            <button
                                                onClick={() => setViewMode('table')}
                                                className={`px-3 py-2 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                                            >
                                                <Table className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => setViewMode('grid')}
                                                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                                            >
                                                <Grid3x3 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {/* Tab Info */}
                            <div className="mb-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900 capitalize">
                                            {tabs.find(t => t.id === activeTab)?.estate} Estate - {tabs.find(t => t.id === activeTab)?.status} Plots
                                        </h2>
                                        <p className="text-gray-600 text-sm">
                                            Showing {Math.min(indexOfLastRow, filteredData.length)} of {filteredData.length} plots
                                            {searchQuery && ` (filtered from ${getCurrentData().length} total)`}
                                        </p>
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {getCurrentLoading() ? 'Loading...' : 'Data loaded'}
                                    </div>
                                </div>
                            </div>

                            {/* Plots Display */}
                            {viewMode === 'table' ? renderTable() : renderGridView()}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ViewSoldAndUnsoldPlots;